import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Switch } from "antd";
import Title from "antd/es/typography/Title";
import { InfoCircleOutlined } from "@ant-design/icons";
import { RankingAPI } from "../../API/RankingAPI";
import {
  buildColumnLayout,
  extractPreferences,
  toApiColumnLayoutKey,
  toApiSortColumnKey,
  toFrozenMap,
  toWidthMap,
} from "../DndTable/gridPreferencesUtils";

const MAX_FROZEN_COLUMNS = 3;

function ColumnRow({ item, onToggle }) {
  return (
    <div className="field-item">
      <div className="field-item-label">{item.label}</div>
      <Switch
        size="small"
        id={`freeze-sw-${item.id}`}
        checked={item.freezeOrder !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
      />
    </div>
  );
}

export const FreezeColumns = ({
  open,
  setOpen,
  messageToast,
  selectedColumns = [],
  setSelectedColumns,
  sortBy,
  sortDir,
  allGridColumnKeys = [],
  defaultWidthMap = {},
  onApplySuccess,
}) => {
  const [items, setItems] = useState([]);
  const [spinning, setSpinning] = useState(false);

  const fetchFreezeConfig = useCallback(async () => {
    setSpinning(true);
    try {
      const [columnResponse, preferencesResponse] = await Promise.all([
        RankingAPI.getColumnConfig(),
        RankingAPI.getRankGridPreferences(),
      ]);

      if (columnResponse?.data?.status !== true) {
        messageToast("error", "Failed to fetch column configuration.");
        return;
      }

      const preferences = extractPreferences(preferencesResponse);
      const frozenMap = toFrozenMap(preferences.column_layout);

      // `selectedColumns` is already the visible set, so hiding a column keeps
      // it out of freeze management -- which is what the notice above the list
      // promises.
      const configColumns = selectedColumns
        .map((column, index) => {
          const isFrozen = Boolean(frozenMap[column.id]);

          return {
            id: column.id,
            label: column.label,
            freezeOrder: isFrozen ? index + 1 : null,
            displayOrder: index + 1,
          };
        })
        .sort((a, b) => {
          const orderA =
            a.freezeOrder !== null
              ? a.freezeOrder
              : a.displayOrder + 10000;
          const orderB =
            b.freezeOrder !== null
              ? b.freezeOrder
              : b.displayOrder + 10000;
          return orderA - orderB;
        })
        .map((column, index) => ({
          ...column,
          freezeOrder: column.freezeOrder !== null ? index + 1 : null,
        }));

      // Enforce max freeze limit even if older preferences exceed it.
      const limitedColumns = (() => {
        let frozenCount = 0;
        return configColumns.map((column) => {
          if (column.freezeOrder === null) {
            return column;
          }
          frozenCount += 1;
          if (frozenCount > MAX_FROZEN_COLUMNS) {
            return { ...column, freezeOrder: null };
          }
          return { ...column, freezeOrder: frozenCount };
        });
      })();

      setItems(limitedColumns);
    } catch {
      messageToast("error", "Failed to fetch freeze column configuration.");
    } finally {
      setSpinning(false);
    }
  }, [messageToast, selectedColumns]);

  useEffect(() => {
    if (open) {
      fetchFreezeConfig();
    }
  }, [open, fetchFreezeConfig]);

  const frozen = useMemo(
    () =>
      items
        .filter((item) => item.freezeOrder !== null)
        .sort((a, b) => a.freezeOrder - b.freezeOrder),
    [items],
  );
  const unfrozen = useMemo(
    () => items.filter((item) => item.freezeOrder === null),
    [items],
  );

  const toggleItem = (id) => {
    setItems((previous) => {
      const target = previous.find((item) => item.id === id);
      if (!target) {
        return previous;
      }

      // Unfreeze is always allowed.
      if (target.freezeOrder !== null) {
        return previous.map((item) =>
          item.id === id ? { ...item, freezeOrder: null } : item,
        );
      }

      const frozenCount = previous.filter(
        (item) => item.freezeOrder !== null,
      ).length;
      if (frozenCount >= MAX_FROZEN_COLUMNS) {
        messageToast(
          "warning",
          `You can freeze up to ${MAX_FROZEN_COLUMNS} columns at a time.`,
          "freeze-limit",
        );
        return previous;
      }

      const maxOrder = Math.max(
        0,
        ...previous.map((entry) => entry.freezeOrder || 0),
      );
      return previous.map((item) =>
        item.id === id ? { ...item, freezeOrder: maxOrder + 1 } : item,
      );
    });
  };

  // Freezing promotes columns to the left of the grid, but that promotion happens at
  // render time only. Store the same order as the column order too, otherwise
  // unfreezing later drops the columns back to their old positions and reads as a
  // silent reshuffle the user never asked for.
  const persistFrozenFirstOrder = useCallback(
    async (frozenIds) => {
      const freezePosition = (column) => frozenIds.indexOf(column.id);
      const reordered = [
        ...selectedColumns
          .filter((column) => frozenIds.includes(column.id))
          .sort((a, b) => freezePosition(a) - freezePosition(b)),
        ...selectedColumns.filter((column) => !frozenIds.includes(column.id)),
      ].map((column, index) => ({ ...column, rank: index + 1 }));

      const bodyData = {
        columns: reordered.map((column) => ({
          // Map the grid key back to the API key (comments -> have_comments).
          column_key: toApiColumnLayoutKey(column.id),
          display_order: column.rank,
        })),
      };

      const response = await RankingAPI.updateColumnConfig(bodyData);
      if (response?.data?.status === true || response?.status === true) {
        setSelectedColumns?.(reordered);
        return true;
      }
      return false;
    },
    [selectedColumns, setSelectedColumns],
  );

  async function applyFun() {
    setSpinning(true);
    try {
      const preferencesResponse = await RankingAPI.getRankGridPreferences();
      const preferences = extractPreferences(preferencesResponse);
      const widthMap = toWidthMap(preferences.column_layout);

      const frozenMap = items.reduce((accumulator, item) => {
        accumulator[item.id] = item.freezeOrder !== null;
        return accumulator;
      }, {});

      const keysToPersist = Array.from(
        new Set([
          ...allGridColumnKeys,
          ...items.map((item) => item.id),
          ...Object.keys(widthMap),
          ...Object.keys(defaultWidthMap),
        ]),
      );

      const columnLayout = buildColumnLayout({
        keysToPersist,
        widthMap,
        renderedWidthMap: {},
        defaultWidthMap,
        frozenMap,
        minWidth: 90,
      });

      const payload = {
        sort_by: toApiSortColumnKey(sortBy) || preferences.sort_by,
        sort_dir: sortDir || preferences.sort_dir,
        column_layout: columnLayout,
      };

      const response = await RankingAPI.updateRankGridPreferences(payload);
      if (response?.data?.status === true || response?.status === true) {
        const frozenIds = frozen.map((item) => item.id);
        const orderSaved = await persistFrozenFirstOrder(frozenIds);

        const frozenColumnCount = frozenIds.length;
        let freezeMessage;
        if (frozenColumnCount === 0) {
          freezeMessage = "No columns have been frozen.";
        } else if (frozenColumnCount === 1) {
          freezeMessage = "Column frozen successfully.";
        } else {
          freezeMessage = "Columns frozen successfully.";
        }
        messageToast("success", freezeMessage);
        onApplySuccess?.(frozenMap);

        if (!orderSaved) {
          messageToast("error", "Failed to update column order.", "freeze-order");
        }
      } else {
        messageToast(
          "error",
          response?.data?.message || "Failed to save freeze column preferences.",
        );
      }
    } catch {
      messageToast("error", "Failed to save freeze column preferences.");
    } finally {
      setSpinning(false);
      setOpen(false);
    }
  }

  return (
    <Modal
      open={open}
      okText="Apply"
      cancelText="Cancel"
      onCancel={() => setOpen(false)}
      onOk={applyFun}
      width={800}
      style={{
        top: 40,
      }}
      footer={[
        <Button key="back" type="text" onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={applyFun}
          loading={spinning}
        >
          Apply
        </Button>,
      ]}
    >
      <div style={{ padding: "25px" }}>
        <div
          style={{ marginBottom: "20px", borderBottom: "solid 1px #80808047" }}
        >
          <Title level={5}>Freeze Columns</Title>
          <p style={{ margin: "0 0 12px", color: "#737373", fontSize: "13px" }}>
            You can freeze up to {MAX_FROZEN_COLUMNS} columns at a time.
          </p>
          <p
            style={{
              margin: "0 0 12px",
              display: "flex",
              alignItems: "flex-start",
              gap: "6px",
              padding: "8px 12px",
              background: "#f0f5ff",
              border: "1px solid #adc6ff",
              borderRadius: "6px",
              color: "#1d39c4",
              fontSize: "12px",
            }}
          >
            <InfoCircleOutlined style={{ marginTop: "2px" }} />
            <span>
              Hidden columns are excluded from freeze management. Make a column
              visible from Add Columns to freeze it.
            </span>
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
          }}
        >
          <div>
            <p className="items-section-title">Frozen</p>
            <div className="fields-overlay-drag-container">
              {frozen.length ? (
                frozen.map((item) => (
                  <ColumnRow key={item.id} item={item} onToggle={toggleItem} />
                ))
              ) : (
                <p className="empty-item">
                  No frozen columns. Toggle columns on the right.
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="items-section-title">Unfrozen</p>
            <div className="fields-overlay-drag-container">
              {unfrozen.length ? (
                unfrozen.map((item) => (
                  <ColumnRow key={item.id} item={item} onToggle={toggleItem} />
                ))
              ) : (
                <p className="empty-item">All columns are frozen.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
